import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';
import { getMsalAuth } from '../AuthProvider';

// Simple per-scope token cache in memory
// key = scopes string joined by space, value = { token, expiryTs }
const tokenCache = new Map();

const SESSION_STORAGE_PREFIX = 'token_cache_';

function getCacheKey(scopes) {
  const normalized = Array.isArray(scopes) && scopes.length > 0 ? [...scopes].sort().join(' ') : (loginRequest?.scopes || []).sort().join(' ');
  return normalized;
}

function saveToSessionStorage(cacheKey, token, expiryTs) {
  try {
    sessionStorage.setItem(SESSION_STORAGE_PREFIX + cacheKey, JSON.stringify({ token, expiryTs }));
  } catch (_e) { /* ignore storage errors */ }
}

function loadFromSessionStorage(cacheKey) {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_PREFIX + cacheKey);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (entry && entry.token && typeof entry.expiryTs === 'number' && Date.now() < entry.expiryTs) {
      return entry;
    }
    // Expired, clean up
    sessionStorage.removeItem(SESSION_STORAGE_PREFIX + cacheKey);
  } catch (_e) { /* ignore parse errors */ }
  return null;
}

function cacheToken(cacheKey, accessToken, expiresOn) {
  if (accessToken && expiresOn) {
    // Refresh 5 minutes before expiry
    const expiryTs = expiresOn - (5 * 60 * 1000);
    tokenCache.set(cacheKey, { token: accessToken, expiryTs });
    saveToSessionStorage(cacheKey, accessToken, expiryTs);
  }
}

export async function getAccessToken(scopes) {
  const effectiveScopes = Array.isArray(scopes) && scopes.length > 0 ? scopes : (loginRequest?.scopes || []);
  const cacheKey = getCacheKey(effectiveScopes);

  // 1. Check in-memory cache (fastest)
  const memEntry = tokenCache.get(cacheKey);
  if (memEntry && memEntry.token && typeof memEntry.expiryTs === 'number' && Date.now() < memEntry.expiryTs) {
    return memEntry.token;
  }

  // 2. Check sessionStorage (survives page refresh)
  const sessionEntry = loadFromSessionStorage(cacheKey);
  if (sessionEntry) {
    // Restore to in-memory cache
    tokenCache.set(cacheKey, sessionEntry);
    return sessionEntry.token;
  }

  // 3. Acquire from MSAL (network/silent call)
  const { instance: msalInstance, accounts } = getMsalAuth();
  if (!msalInstance || !accounts || accounts.length === 0) return null;
  const account = accounts[0];

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      scopes: effectiveScopes,
      account,
    });

    const accessToken = result?.accessToken || null;
    const expiresOn = result?.expiresOn instanceof Date ? result.expiresOn.getTime() : null;
    cacheToken(cacheKey, accessToken, expiresOn);
    return accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      try {
        const result = await msalInstance.acquireTokenPopup({
          ...loginRequest,
          scopes: effectiveScopes,
          account,
        });

        const accessToken = result?.accessToken || null;
        const expiresOn = result?.expiresOn instanceof Date ? result.expiresOn.getTime() : null;
        cacheToken(cacheKey, accessToken, expiresOn);
        return accessToken;
      } catch (popupErr) {
        console.error('Token popup failed', popupErr);
        return null;
      }
    }
    console.error('Silent token acquisition failed', err);
    return null;
  }
}

export function clearTokenCache() {
  tokenCache.clear();
  // Also clear sessionStorage token entries
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(SESSION_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch (_e) { /* ignore */ }
}


